/**
 * NotionPickerModal — V2 port of V1 ``openNotionPicker`` (May 25, 2026).
 *
 * Notion ships no native picker SDK; V1 rendered a DOM overlay with a
 * searchable list of accessible pages/databases (the user picks one or
 * more, click "Aggiungi"). V2 keeps the same UX but as a Radix Drawer
 * for visual consistency with the OneDrive/SharePoint flow.
 *
 * Pages are surfaced as ``PickedItem`` (``is_folder: true`` is a slight
 * stretch — Notion pages can contain sub-pages so they behave more like
 * folders than files for the tracking model. The BE persists them as
 * folder rows and walks their contents on sync, mirroring V1).
 */

import {
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  cn,
} from "@ando/ui";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { BookOpen, FileText, Loader2, Search } from "lucide-react";
import * as React from "react";

import { useNotionWorkspacePages } from "@/features/sync/hooks/useDriveSync";

import type { PickedItem } from "./types";

export interface NotionPickerModalProps {
  readonly open: boolean;
  readonly connectionKey?: string;
  readonly onPick: (items: readonly PickedItem[]) => void;
  readonly onCancel: () => void;
}

export const NotionPickerModal: React.FC<NotionPickerModalProps> = ({ open, onPick, onCancel }) => {
  const { t } = useLingui();
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [selected, setSelected] = React.useState<
    Map<string, { name: string; type: "page" | "database" }>
  >(() => new Map());

  // Reset every open — V1 parity.
  React.useEffect(() => {
    if (open) {
      setQuery("");
      setDebouncedQuery("");
      setSelected(new Map());
    }
  }, [open]);

  // Debounced search input → reduces calls to the workspace search relay.
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(id);
  }, [query]);

  const pages = useNotionWorkspacePages(debouncedQuery.trim() || undefined, open);

  const toggle = (id: string, name: string, type: "page" | "database"): void =>
    setSelected((s) => {
      const next = new Map(s);
      if (next.has(id)) next.delete(id);
      else next.set(id, { name, type });
      return next;
    });

  const handleConfirm = (): void => {
    const items: PickedItem[] = Array.from(selected.entries()).map(([id, { name }]) => ({
      id,
      name,
      is_folder: true, // Notion pages persist as folder-shaped tracked entries
      provider: "notion",
    }));
    onPick(items);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DrawerContent
        side="right"
        ariaTitle={t`Aggiungi pagine da Notion`}
        className="flex w-[min(460px,95vw)] flex-col sm:max-w-none"
      >
        <DrawerHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-ando-orange-soft text-ando-orange">
              <BookOpen className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-display font-semibold text-[15px] text-ink-1">
                <Trans>Aggiungi da Notion</Trans>
              </h2>
              <p className="truncate text-[11.5px] text-ink-3">
                <Trans>Seleziona pagine e database del workspace</Trans>
              </p>
            </div>
          </div>
        </DrawerHeader>

        <DrawerBody className="flex-1 overflow-y-auto px-5 py-4">
          {/* Search */}
          <div className="relative mb-3">
            <Search className="-translate-y-1/2 absolute top-1/2 left-2 h-3.5 w-3.5 text-ink-3" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t`Cerca pagine, database…`}
              className="w-full rounded-md border border-hairline bg-white/55 py-1.5 pr-2.5 pl-7 text-[12px] text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
            />
          </div>

          {/* List */}
          {pages.isLoading ? (
            <div className="flex items-center gap-2 text-[12px] text-ink-3">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <Trans>Carico contenuti…</Trans>
            </div>
          ) : !pages.data || pages.data.length === 0 ? (
            <p className="text-[12px] text-ink-3">
              {debouncedQuery
                ? t`Nessuna pagina corrisponde alla ricerca.`
                : t`Nessuna pagina accessibile in questo workspace.`}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {pages.data.map((p) => {
                const checked = selected.has(p.id);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => toggle(p.id, p.title, p.type)}
                      aria-pressed={checked}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left",
                        checked
                          ? "border-ando-orange/45 bg-ando-orange-soft"
                          : "border-hairline bg-card/50 hover:bg-card/85",
                      )}
                    >
                      <Checkbox checked={checked} readOnly />
                      {p.type === "database" ? (
                        <BookOpen className="h-3.5 w-3.5 text-ink-3" />
                      ) : (
                        <FileText className="h-3.5 w-3.5 text-ink-3" />
                      )}
                      <span className="min-w-0 flex-1 truncate text-[12px] text-ink-1">
                        {p.title}
                      </span>
                      <span className="shrink-0 text-[10px] text-ink-3">
                        {p.type === "database" ? <Trans>DB</Trans> : <Trans>pagina</Trans>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DrawerBody>

        <DrawerFooter className="flex flex-col gap-2">
          <p className="text-[11.5px] text-ink-3">
            <Plural
              value={selected.size}
              one="# elemento selezionato"
              other="# elementi selezionati"
            />
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md px-3 py-1.5 font-medium text-[12px] text-ink-2 hover:bg-black/[0.04]"
            >
              <Trans>Annulla</Trans>
            </button>
            <button
              type="button"
              disabled={selected.size === 0}
              onClick={handleConfirm}
              className={cn(
                "flex-1 rounded-md bg-ando-orange px-3 py-1.5 font-medium text-[12px] text-white",
                "hover:bg-ando-orange/90 disabled:cursor-not-allowed disabled:bg-ink-3/30 disabled:text-ink-3",
              )}
            >
              {selected.size === 0 ? t`Seleziona almeno una pagina` : t`Aggiungi ${selected.size}`}
            </button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
NotionPickerModal.displayName = "NotionPickerModal";
